import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import { convertTo12HourFormat } from "../../common-data/Converts";

function AttendanceExportButton({ filters }) {
  const { staff_atttendance_report } = useSelector((state) => state.api);
  const [exporting, setExporting] = useState(false);

  const rows = staff_atttendance_report?.status
    ? staff_atttendance_report?.data || []
    : [];

  const safeTime = (t) =>
    t && t !== "N/A" ? convertTo12HourFormat(t) : "";

  const normalizeStatus = (s) =>
    (s || "").toLowerCase().replace(/ /g, "_");

  const buildShiftCols = (shift) => {
    if (!shift) {
      return {
        name: "", window: "", status: "", checkin: "", checkout: "", note: "",
      };
    }
    const start = shift.start_time ? convertTo12HourFormat(shift.start_time) : "";
    const end = shift.end_time ? convertTo12HourFormat(shift.end_time) : "";
    const ad = shift.attendance_details || {};
    const diff = ad.shift_difference && ad.shift_difference !== "N/A"
      ? ad.shift_difference : "";
    const lateOnTime = ad.status && ad.status !== "N/A" ? ad.status : "";
    const note = diff && lateOnTime && lateOnTime !== "On Time"
      ? `${lateOnTime} ${diff}` : lateOnTime;
    return {
      name: shift.shift_name || "",
      window: start || end ? `${start || "-"} - ${end || "-"}` : "",
      status: ad.checkin_status || "",
      checkin: safeTime(ad.checkin_time),
      checkout: safeTime(ad.checkout_time),
      note,
    };
  };

  const isCheckedInForDay = (row) =>
    (row?.shift_details || []).some(
      (s) => normalizeStatus(s?.attendance_details?.checkin_status) === "checked-in"
    );

  const buildAttendanceSheet = () =>
    rows.map((r) => {
      const shifts = r?.shift_details || [];
      const s1 = buildShiftCols(shifts[0]);
      const s2 = buildShiftCols(shifts[1]);
      const role = r?.account_type === "receptionist"
        ? "Sales Executive (FD)"
        : (r?.account_type || "").replace(/_/g, " ");
      return {
        "Name": r?.name || "",
        "Mobile": r?.mobile || "",
        "Email": r?.email || "",
        "Staff Type": role,
        "Gym Name": r?.gym_name || "",
        "Shift 1": s1.name,
        "Shift 1 Window": s1.window,
        "Shift 1 Status": s1.status,
        "Shift 1 Check-In": s1.checkin,
        "Shift 1 Check-Out": s1.checkout,
        "Shift 1 Note": s1.note,
        "Shift 2": s2.name,
        "Shift 2 Window": s2.window,
        "Shift 2 Status": s2.status,
        "Shift 2 Check-In": s2.checkin,
        "Shift 2 Check-Out": s2.checkout,
        "Shift 2 Note": s2.note,
        "Date From": filters?.date_from || "",
        "Date To": filters?.date_to || "",
      };
    });

  const buildGymWiseSheet = () => {
    const byGym = {};
    rows.forEach((r) => {
      const gym = r?.gym_name || "Unknown";
      if (!byGym[gym]) {
        byGym[gym] = { fdTotal: 0, fdIn: 0, trainerTotal: 0, trainerIn: 0 };
      }
      const checkedIn = isCheckedInForDay(r);
      if (r?.account_type === "receptionist") {
        byGym[gym].fdTotal += 1;
        if (checkedIn) byGym[gym].fdIn += 1;
      } else if (r?.account_type === "trainer") {
        byGym[gym].trainerTotal += 1;
        if (checkedIn) byGym[gym].trainerIn += 1;
      }
    });
    return Object.entries(byGym)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([gym, c]) => ({
        "Gym Name": gym,
        "FD Check-ins": `${c.fdIn}/${c.fdTotal}`,
        "Trainer Check-ins": `${c.trainerIn}/${c.trainerTotal}`,
      }));
  };

  const handleExport = () => {
    if (rows.length === 0) return;
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();

      const ws1 = XLSX.utils.json_to_sheet(buildAttendanceSheet());
      ws1["!cols"] = [
        { wch: 24 }, { wch: 14 }, { wch: 28 }, { wch: 22 }, { wch: 32 },
        { wch: 12 }, { wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 24 },
        { wch: 12 }, { wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 24 },
        { wch: 12 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, "Attendance");

      const ws2 = XLSX.utils.json_to_sheet(buildGymWiseSheet());
      ws2["!cols"] = [{ wch: 40 }, { wch: 18 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Gym Wise Count");

      const from = filters?.date_from || "";
      const to = filters?.date_to || "";
      const datePart = from && to
        ? (from === to ? from : `${from}_to_${to}`)
        : new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Attendance_Report_${datePart}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const isDisabled = exporting || rows.length === 0;

  return (
    <Button
      onClick={handleExport}
      disabled={isDisabled}
      style={{
        backgroundColor: "#CC0A39",
        borderColor: "#CC0A39",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: 0.3,
        padding: "8px 18px",
        borderRadius: 8,
        boxShadow: "0 2px 6px rgba(204, 10, 57, 0.25)",
        opacity: isDisabled ? 0.55 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        width="16"
        height="16"
        style={{ marginRight: 8, verticalAlign: "middle" }}
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>
      {exporting ? "Exporting..." : "Export"}
    </Button>
  );
}

export default AttendanceExportButton;
